import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BsTagFill } from 'react-icons/bs';
import Modal from '../../../Modal';
import { getVirtuosoPromotionsRequest } from '../../../../store/slices/cruise';
import { SearchStatus } from '../../../../constants';
import withSpinner from '../../../../HOCs/withSpinner';

/**
 * Virtuoso promotion detail for one sailing.
 *
 * Details are fetched when the modal opens rather than with the search results:
 * the Virtuoso detail endpoint is slow on a cold cache, so the cost is only paid
 * for the sailing an advisor actually opens.
 */
const VirtuosoPromotionsModal = ({ virtuosoVoyage, open, onClose }) => {
  const dispatch = useDispatch();
  const { virtuosoPromotions, virtuosoPromotionsError, getVirtuosoPromotionsStatus } = useSelector(
    (store) => store.cruise,
  );

  useEffect(() => {
    if (open) dispatch(getVirtuosoPromotionsRequest(virtuosoVoyage.id));
  }, [dispatch, open, virtuosoVoyage.id]);

  const promotions = virtuosoPromotions?.promotions || [];
  const sailings = virtuosoPromotions?.sailings || [];

  return (
    <Modal open={open} onClose={onClose}>
      <div className="p-8">
        <div className="text-theme font-bold sm:text-sm xl:text-lg flex items-center">
          <BsTagFill className="mr-2" />
          Virtuoso promotions
        </div>

        {virtuosoPromotions && (
          <div className="text-sm text-gray-600 mt-1">
            {[
              virtuosoPromotions.cruiseName,
              virtuosoPromotions.shipName,
              virtuosoPromotions.travelDates,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}

        {withSpinner(
          () => (
            <div className="mt-5">
              {virtuosoPromotionsError ? (
                <div className="text-sm">
                  {virtuosoPromotionsError}. Full details are available on{' '}
                  <a
                    href={virtuosoVoyage.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-theme underline"
                  >
                    virtuoso.com
                  </a>
                  .
                </div>
              ) : (
                <>
                  {promotions.map((promotion) => (
                    <div key={promotion.id} className="border-l-4 border-red-600 pl-4 mb-6">
                      <div className="font-bold">
                        {promotion.name}
                        {promotion.isVirtuosoExclusiveExperience && (
                          <span className="ml-2 text-xs font-bold uppercase text-theme border border-current rounded px-1 py-0.5">
                            Virtuoso exclusive
                          </span>
                        )}
                        {promotion.isAdvisorIncentive && (
                          <span className="ml-2 text-xs font-bold uppercase text-theme border border-current rounded px-1 py-0.5">
                            Advisor incentive
                          </span>
                        )}
                      </div>
                      {promotion.validity && (
                        <div className="text-xs text-gray-600 mt-1">{promotion.validity}</div>
                      )}
                      {promotion.descriptionHtml && (
                        <div
                          className="text-sm mt-2 prose max-w-none"
                          /* Supplier-authored offer copy, delivered as HTML by the Virtuoso API. */
                          // oxlint-disable-next-line no-danger
                          dangerouslySetInnerHTML={{ __html: promotion.descriptionHtml }}
                        />
                      )}
                      {promotion.whatIsIncludedHtml && (
                        <div
                          className="text-sm mt-2 prose max-w-none"
                          // oxlint-disable-next-line no-danger
                          dangerouslySetInnerHTML={{ __html: promotion.whatIsIncludedHtml }}
                        />
                      )}
                    </div>
                  ))}

                  {sailings.length > 1 && (
                    <div className="mt-6">
                      <div className="font-bold mb-2">Other departures</div>
                      <ul className="text-sm">
                        {sailings.map((sailing) => (
                          <li key={sailing.id} className="flex items-center gap-2 py-0.5">
                            <span
                              className={
                                sailing.hasPromotion
                                  ? 'w-2 h-2 rounded-full bg-red-600 shrink-0'
                                  : 'w-2 h-2 rounded-full bg-gray-300 shrink-0'
                              }
                            />
                            <a
                              href={sailing.url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-theme underline"
                            >
                              {sailing.dates}
                            </a>
                            {sailing.hasBenefit && (
                              <span className="text-xs font-bold text-theme">BENEFITS</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <a
                    href={virtuosoVoyage.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-theme underline text-sm inline-block mt-6"
                  >
                    View this sailing on virtuoso.com
                  </a>
                </>
              )}
            </div>
          ),
          getVirtuosoPromotionsStatus === SearchStatus.SEARCHING,
          'Loading Virtuoso promotions...',
        )}
      </div>
    </Modal>
  );
};

VirtuosoPromotionsModal.propTypes = {
  virtuosoVoyage: PropTypes.shape().isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default VirtuosoPromotionsModal;
